import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Plus,
  FileText,
  UserCog,
  Settings,
  TerminalSquare,
  BookOpen,
  X,
  Zap,
  Users,
  Shield,
  Activity
} from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';

interface QuickActionsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ActionItem {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  action?: () => void;
  requiresSuperAdmin?: boolean;
  shortcut?: string;
  loading?: boolean;
}

export function QuickActionsDrawer({ open, onOpenChange }: QuickActionsDrawerProps) {
  const navigate = useNavigate();
  const { isSuperAdmin } = useUserRole();
  const [loadingActions, setLoadingActions] = useState<Set<string>>(new Set());

  // Define available actions
  const allActions: ActionItem[] = [
    {
      id: 'add-church',
      label: 'Add New Church',
      description: 'Register a new church organization',
      icon: Plus,
      href: '/churches/new',
      requiresSuperAdmin: true,
      shortcut: 'N'
    },
    {
      id: 'generate-report',
      label: 'Generate Global Report',
      description: 'Create comprehensive platform analytics',
      icon: FileText,
      action: () => handleGenerateReport(),
      requiresSuperAdmin: true,
      shortcut: 'R'
    },
    {
      id: 'manage-admins',
      label: 'Manage SuperAdmins',
      description: 'Configure platform administrators',
      icon: UserCog,
      href: '/superadmin/users',
      requiresSuperAdmin: true,
      shortcut: 'U'
    },
    {
      id: 'system-settings',
      label: 'System Settings',
      description: 'Configure platform-wide settings',
      icon: Settings,
      href: '/superadmin/settings',
      requiresSuperAdmin: true,
      shortcut: 'S'
    },
    {
      id: 'system-logs',
      label: 'View System Logs',
      description: 'Monitor platform activity and errors',
      icon: TerminalSquare,
      href: '/logs',
      shortcut: 'L'
    },
    {
      id: 'docs-help',
      label: 'Docs / Help',
      description: 'Access documentation and support',
      icon: BookOpen,
      href: '/help',
      shortcut: 'H'
    }
  ];

  // Filter actions based on user permissions
  const availableActions = allActions.filter(action => 
    !action.requiresSuperAdmin || isSuperAdmin
  );

  // Handle report generation
  const handleGenerateReport = async () => {
    const actionId = 'generate-report';
    setLoadingActions(prev => new Set([...prev, actionId]));
    
    try {
      // Call the edge function for report generation
      const { data, error } = await supabase.functions.invoke('generate-report', {
        body: {
          type: 'global',
          format: 'csv'
        }
      });

      if (error) {
        console.error('Error generating report:', error);
        // You might want to show a toast notification here
      } else {
        console.log('Report generated successfully:', data);
        // Handle successful report generation
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoadingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(actionId);
        return newSet;
      });
    }
  };

  // Handle action execution
  const handleAction = (action: ActionItem) => {
    if (action.loading || loadingActions.has(action.id)) return;

    if (action.action) {
      action.action();
    } else if (action.href) {
      navigate(action.href);
      onOpenChange(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
        return;
      }

      // Handle shortcuts
      if (e.shiftKey) return; // Ignore when shift is held (for the trigger)
      
      const action = availableActions.find(a => 
        a.shortcut?.toLowerCase() === e.key.toLowerCase()
      );
      
      if (action) {
        e.preventDefault();
        handleAction(action);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, availableActions]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[28rem] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Quick Actions
            </SheetTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="text-sm text-muted-foreground mb-4">
            Press keyboard shortcuts or click to execute actions. ESC to close.
          </div>

          <div className="grid gap-3">
            {availableActions.map((action) => {
              const isLoading = loadingActions.has(action.id);
              const IconComponent = action.icon;
              const hasPermission = !action.requiresSuperAdmin || isSuperAdmin;

              return (
                <Card 
                  key={action.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    !hasPermission ? 'opacity-50 cursor-not-allowed' : ''
                  } ${isLoading ? 'opacity-75' : ''}`}
                  onClick={() => hasPermission && !isLoading && handleAction(action)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 p-3 rounded-lg bg-primary/10">
                        <IconComponent className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-sm">{action.label}</h3>
                          {action.shortcut && (
                            <Badge variant="secondary" className="text-xs">
                              {action.shortcut}
                            </Badge>
                          )}
                          {isLoading && (
                            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {action.description}
                        </p>
                      </div>
                      {!hasPermission && (
                        <div className="flex-shrink-0">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Footer info */}
          <div className="mt-8 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-start gap-3">
              <Activity className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p className="font-medium mb-1">Keyboard Shortcuts:</p>
                <div className="space-y-1">
                  {availableActions.slice(0, 3).map(action => (
                    <div key={action.id} className="flex justify-between">
                      <span>{action.label}</span>
                      <kbd className="px-1 py-0.5 bg-background border rounded text-xs">
                        {action.shortcut}
                      </kbd>
                    </div>
                  ))}
                </div>
                <p className="mt-2">Press Shift + A to reopen this drawer anytime.</p>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
